import { z } from 'zod';
import { ACTIVITY_TYPES } from '../enums';
import { isoDate, isoDateTime, uuid } from './common';
import { itemKey } from './work-items';

/** Log time (FR-TIME-2, FR-TIME-3, FR-PROD-4). Either `duration` text or `durationMin` is required. */
export const createWorklogSchema = z
  .object({
    duration: z.string().max(40).optional(),
    durationMin: z.number().int().min(1).max(24 * 60).optional(),
    workDate: isoDate,
    startedAt: isoDateTime.optional(),
    endedAt: isoDateTime.optional(),
    description: z.string().max(2000).optional(),
    activityType: z.enum(ACTIVITY_TYPES).default('development'),
    billable: z.boolean().optional(),
    remainingMode: z.enum(['auto', 'set', 'leave']).default('auto'),
    remainingMin: z.number().int().min(0).optional(),
  })
  .refine((v) => v.duration || v.durationMin || (v.startedAt && v.endedAt), {
    message: 'Duration is required',
    path: ['duration'],
  })
  .refine((v) => v.remainingMode !== 'set' || v.remainingMin !== undefined, {
    message: 'remainingMin is required when remainingMode is "set"',
    path: ['remainingMin'],
  });
export type CreateWorklogInput = z.infer<typeof createWorklogSchema>;

export const updateWorklogSchema = z.object({
  duration: z.string().max(40).optional(),
  durationMin: z.number().int().min(1).max(24 * 60).optional(),
  workDate: isoDate.optional(),
  startedAt: isoDateTime.nullable().optional(),
  endedAt: isoDateTime.nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  activityType: z.enum(ACTIVITY_TYPES).optional(),
  billable: z.boolean().optional(),
});
export type UpdateWorklogInput = z.infer<typeof updateWorklogSchema>;

export const startTimerSchema = z.object({ itemKey });
export const stopTimerSchema = z.object({
  description: z.string().max(2000).optional(),
  activityType: z.enum(ACTIVITY_TYPES).optional(),
  discard: z.boolean().optional(),
});
export type StopTimerInput = z.infer<typeof stopTimerSchema>;

/** One-step "Log & complete" for quick tasks (FR-QT-4). */
export const logAndCompleteSchema = z.object({
  duration: z.string().max(40).optional(),
  durationMin: z.number().int().min(1).optional(),
  workDate: isoDate,
  description: z.string().max(2000).optional(),
  activityType: z.enum(ACTIVITY_TYPES).optional(),
});
export type LogAndCompleteInput = z.infer<typeof logAndCompleteSchema>;

/** Org time settings (FR-TIME-11). */
export const orgTimeSettingsSchema = z.object({
  hoursPerDay: z.number().min(1).max(24).default(8),
  daysPerWeek: z.number().int().min(1).max(7).default(5),
  holidays: z.array(z.object({ date: isoDate, name: z.string() })).default([]),
  warnDailyHours: z.number().min(1).max(24).default(10),
  backdateDays: z.number().int().min(0).max(365).default(14),
  underLoggedHours: z.number().min(0).max(24).default(6),
  overLoggedHours: z.number().min(0).max(24).default(9),
  /** Weekly timesheet reminder (ISO weekday 1–7 and local hour) for weeks not yet submitted. */
  timesheetReminderDay: z.number().int().min(1).max(7).default(5),
  timesheetReminderHour: z.number().int().min(0).max(23).default(16),
});
export type OrgTimeSettings = z.infer<typeof orgTimeSettingsSchema>;

export const utilisationQuery = z.object({ from: isoDate, to: isoDate, projectId: uuid.optional() });

// Timesheets (FR-TIME-5, FR-TIME-10, FR-PROD-7) ---------------------------------------------------

export const timesheetWeekQuery = z.object({ week: isoDate, userId: uuid.optional() });

/** Weekly grid edits: a cell is the user's total on (item, date); 0 removes it. */
export const timesheetCellsSchema = z.object({
  week: isoDate,
  cells: z.array(z.object({ itemKey, date: isoDate, minutes: z.number().int().min(0).max(24 * 60) })).min(1).max(300),
  activityType: z.enum(ACTIVITY_TYPES).optional(),
});
export type TimesheetCellsInput = z.infer<typeof timesheetCellsSchema>;

export const submitTimesheetSchema = z.object({
  week: isoDate,
  /** Test results executed without a worklog block submission until acknowledged (FR-PROD-7). */
  acknowledgeUnlogged: z.boolean().default(false),
  comment: z.string().max(2000).optional(),
});
export const timesheetDecisionSchema = z.object({ comment: z.string().max(2000).optional() });
export const rejectTimesheetSchema = z.object({ comment: z.string().min(1, 'Say what needs to change').max(2000) });

/** Capacity-aware picker (FR-ASG-15). */
export const capacityQuery = z.object({ sprintId: uuid.optional(), from: isoDate.optional(), to: isoDate.optional() });
